import React, { useState } from 'react';
import { Modal, Input, Radio, Button, Tag, Alert } from 'antd';
import { toast } from 'react-hot-toast';
import { bulkCreateServiceabilityService } from '../../../core/services/serviceabilityService';
import { useSelector } from 'react-redux';

interface Props {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const ServiceabilityCreateModal: React.FC<Props> = ({ visible, onClose, onSuccess }) => {
    const { user } = useSelector((s: any) => s.authStore);

    const [msnText, setMsnText] = useState('');
    const [pincodeText, setPincodeText] = useState('');
    const [isCod, setIsCod] = useState<boolean>(true);
    const [isServiceable, setIsServiceable] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [invalidResult, setInvalidResult] = useState<{
        invalidMsnList?: string[];
        invalidPincodeList?: string[];
    } | null>(null);

    const sanitizeInputList = (text: string): string[] => {
        return text
            .split(',')
            .map(s => s.trim().replace(/^["']+|["']+$/g, '').trim())
            .filter(Boolean);
    };

    const parsedMsns = sanitizeInputList(msnText);
    const parsedPincodes = sanitizeInputList(pincodeText);
    const nonNumericPincodes = parsedPincodes.filter(p => !/^\d+$/.test(p));
    const hasNonNumericPincodes = nonNumericPincodes.length > 0;
    const totalRecords = parsedMsns.length * parsedPincodes.length;

    const handleReset = () => {
        setMsnText('');
        setPincodeText('');
        setIsCod(true);
        setIsServiceable(true);
        setInvalidResult(null);
    };

    const handleModalClose = () => {
        handleReset();
        onClose();
    };

    const handleSubmit = async () => {
        const msns = sanitizeInputList(msnText);
        const pincodes = sanitizeInputList(pincodeText);

        if (msns.length === 0) {
            toast.error('Please enter at least one MSN (comma-separated).');
            return;
        }

        if (msns.length > 500) {
            toast.error(`Maximum 500 MSNs allowed. You have entered ${msns.length} MSNs.`);
            return;
        }

        if (pincodes.length === 0) {
            toast.error('Please enter at least one Pincode (comma-separated).');
            return;
        }

        if (pincodes.length > 500) {
            toast.error(`Maximum 500 Pincodes allowed. You have entered ${pincodes.length} Pincodes.`);
            return;
        }

        const nonNumericList = pincodes.filter(p => !/^\d+$/.test(p));
        if (nonNumericList.length > 0) {
            toast.error(
                `Pincodes must be numbers only. Found ${nonNumericList.length} invalid item${nonNumericList.length === 1 ? '' : 's'}: ${nonNumericList.slice(0, 3).join(', ')}${nonNumericList.length > 3 ? '...' : ''}`
            );
            return;
        }

        const email = user?.email || 'deepak.singh1@moglix.com';
        setLoading(true);
        setInvalidResult(null);

        let hasTimedOut10s = false;
        const tenSecondTimer = setTimeout(() => {
            hasTimedOut10s = true;
            setLoading(false);
            handleModalClose();
            toast(
                'Creation is in progress. Please do not re-upload the same MSNs, and check after some time.',
                {
                    icon: '⏳',
                    duration: 10000,
                    style: {
                        borderRadius: '8px',
                        background: '#1e293b',
                        color: '#ffffff',
                        fontWeight: 500,
                        padding: '14px 18px',
                    },
                }
            );
            // Refresh listing table after a delay
            setTimeout(() => {
                onSuccess();
            }, 4000);
        }, 10000);

        try {
            const payload = {
                msnList: msns,
                categoryList: [],
                pincodeList: pincodes,
                isCod,
                isServiceable,
            };

            const res = await bulkCreateServiceabilityService(payload, email);
            clearTimeout(tenSecondTimer);

            const resData = res?.data;

            const isSuccess =
                resData?.statusCode === 200 ||
                resData?.status === true ||
                resData?.status === 200 ||
                res?.status === 200;

            if (isSuccess) {
                const invalidData = resData?.data;
                const hasInvalidMsns = Array.isArray(invalidData?.invalidMsnList) && invalidData.invalidMsnList.length > 0;
                const hasInvalidPincodes = Array.isArray(invalidData?.invalidPincodeList) && invalidData.invalidPincodeList.length > 0;

                if (hasTimedOut10s) {
                    // Modal was already closed after 10s
                    if (hasInvalidMsns || hasInvalidPincodes) {
                        toast(
                            `Rules created in background with some skipped items (${invalidData?.invalidMsnList?.length || 0} invalid MSNs, ${invalidData?.invalidPincodeList?.length || 0} invalid pincodes).`,
                            { icon: '⚠️', duration: 8000 }
                        );
                    } else {
                        toast.success(resData?.statusDescription || 'Serviceability rules created successfully!', { duration: 6000 });
                    }
                    onSuccess();
                } else {
                    if (hasInvalidMsns || hasInvalidPincodes) {
                        setInvalidResult({
                            invalidMsnList: invalidData.invalidMsnList || [],
                            invalidPincodeList: invalidData.invalidPincodeList || [],
                        });
                        onSuccess();
                    } else {
                        toast.success(resData?.statusDescription || 'Serviceability rules created successfully!');
                        handleModalClose();
                        onSuccess();
                    }
                }
            } else {
                if (!hasTimedOut10s) {
                    toast.error(resData?.message || resData?.statusDescription || 'Failed to create serviceability rules.');
                }
            }
        } catch (err: any) {
            clearTimeout(tenSecondTimer);
            if (hasTimedOut10s) {
                return;
            }

            const isTimeout =
                err?.code === 'ECONNABORTED' ||
                err?.response?.status === 504 ||
                err?.message?.toLowerCase().includes('timeout');

            if (isTimeout) {
                toast(
                    'Creation is in progress. Please do not re-upload the same MSNs, and check after some time.',
                    {
                        icon: '⏳',
                        duration: 10000,
                        style: {
                            borderRadius: '8px',
                            background: '#1e293b',
                            color: '#ffffff',
                            fontWeight: 500,
                            padding: '14px 18px',
                        },
                    }
                );
                handleModalClose();
                setTimeout(() => {
                    onSuccess();
                }, 3000);
            } else {
                toast.error(err?.response?.data?.message || err?.response?.data?.statusDescription || err?.message || 'Error creating serviceability rules.');
            }
        } finally {
            if (!hasTimedOut10s) {
                setLoading(false);
            }
        }
    };

    return (
        <Modal
            title={
                <div className="text-xl font-bold text-[#43425D]">
                    Create Serviceability Rule
                </div>
            }
            open={visible}
            onCancel={loading ? undefined : handleModalClose}
            maskClosable={!loading}
            closable={!loading}
            width={1280}
            centered
            footer={[
                <Button key="cancel" disabled={loading} onClick={handleModalClose} size="large" className="px-6">
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    disabled={loading || parsedMsns.length > 500 || parsedPincodes.length > 500 || hasNonNumericPincodes}
                    onClick={handleSubmit}
                    size="large"
                    className="bg-blue-600 hover:bg-blue-700 px-7 font-semibold"
                >
                    {loading ? 'Creating Rules...' : 'Create'}
                </Button>
            ]}
        >
            <div className="flex flex-col gap-4 py-2 max-h-[75vh] overflow-y-auto pr-2">
                {/* Invalid results banner if any items were rejected */}
                {invalidResult && (
                    <Alert
                        type="warning"
                        showIcon
                        closable
                        onClose={() => setInvalidResult(null)}
                        message={<span className="font-semibold text-xs">Partial Save Warning</span>}
                        description={
                            <div className="text-xs flex flex-col gap-1 mt-0.5 text-gray-700">
                                <span>Valid combinations saved. The following items could not be verified:</span>
                                {invalidResult.invalidMsnList && invalidResult.invalidMsnList.length > 0 && (
                                    <div className="text-red-600 leading-normal">
                                        <span className="font-semibold text-gray-800">Invalid MSNs ({invalidResult.invalidMsnList.length}): </span>
                                        <span className="font-mono text-[11px] select-all bg-amber-50/50 px-1 py-0.5 rounded border border-amber-200/60">
                                            {invalidResult.invalidMsnList.join(', ')}
                                        </span>
                                    </div>
                                )}
                                {invalidResult.invalidPincodeList && invalidResult.invalidPincodeList.length > 0 && (
                                    <div className="text-red-600 leading-normal">
                                        <span className="font-semibold text-gray-800">Invalid Pincodes ({invalidResult.invalidPincodeList.length}): </span>
                                        <span className="font-mono text-[11px] select-all bg-amber-50/50 px-1 py-0.5 rounded border border-amber-200/60">
                                            {invalidResult.invalidPincodeList.join(', ')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        }
                    />
                )}

                {/* Functionality description & total records note */}
                <div className="text-xs text-gray-600 bg-blue-50/70 p-3 rounded-lg border border-blue-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                        <div>
                            <span className="font-semibold text-gray-800">Bulk Serviceability Rule Creation: </span>
                            <span>
                                Quickly create or update serviceability and COD rules across combinations of MSNs and Pincodes. Each MSN entered will be mapped against all specified pincodes with the chosen settings.
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        <span className="text-gray-500 font-medium">Total Records:</span>
                        <Tag color={totalRecords > 0 ? 'green' : 'default'} className="font-semibold text-xs px-2.5 py-0.5 m-0">
                            {totalRecords > 0 ? `${totalRecords.toLocaleString()} (${parsedMsns.length} MSNs × ${parsedPincodes.length} Pincodes)` : '0 records'}
                        </Tag>
                    </div>
                </div>

                {/* 2-Column Inputs: MSN on left, Pincode on right */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* MSN Input */}
                    <div className="flex flex-col">
                        <div className="flex justify-between items-center mb-1.5">
                            <label className="text-sm font-semibold text-gray-800">
                                MSN List (Comma-separated, max 500) <span className="text-red-500">*</span>
                            </label>
                            <Tag
                                color={parsedMsns.length > 500 ? 'red' : parsedMsns.length > 0 ? 'blue' : 'default'}
                                className="text-xs px-2 py-0.5 font-medium m-0"
                            >
                                {parsedMsns.length} / 500 MSN{parsedMsns.length === 1 ? '' : 's'} {parsedMsns.length > 500 && '(Exceeded!)'}
                            </Tag>
                        </div>
                        <Input.TextArea
                            rows={10}
                            placeholder="e.g. MSN001, MSN002, MSN003"
                            value={msnText}
                            disabled={loading}
                            onChange={(e) => setMsnText(e.target.value)}
                            status={parsedMsns.length > 500 ? 'error' : undefined}
                            className="text-xs font-mono p-2.5 rounded-md border-gray-300 resize-none"
                        />
                        <span className={`text-[11px] mt-1 block ${parsedMsns.length > 500 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                            {parsedMsns.length > 500
                                ? `Exceeded limit of 500 MSNs (${parsedMsns.length} entered). Please remove ${parsedMsns.length - 500} MSNs.`
                                : 'Enter up to 500 valid MSNs separated only by commas.'}
                        </span>
                    </div>

                    {/* Pincode Input */}
                    <div className="flex flex-col">
                        <div className="flex justify-between items-center mb-1.5">
                            <label className="text-sm font-semibold text-gray-800">
                                Pincode List (Numbers only, max 500) <span className="text-red-500">*</span>
                            </label>
                            <Tag
                                color={parsedPincodes.length > 500 || hasNonNumericPincodes ? 'red' : parsedPincodes.length > 0 ? 'geekblue' : 'default'}
                                className="text-xs px-2 py-0.5 font-medium m-0"
                            >
                                {hasNonNumericPincodes
                                    ? `${nonNumericPincodes.length} Non-numeric`
                                    : `${parsedPincodes.length} / 500 Pincode${parsedPincodes.length === 1 ? '' : 's'} ${parsedPincodes.length > 500 ? '(Exceeded!)' : ''}`}
                            </Tag>
                        </div>
                        <Input.TextArea
                            rows={10}
                            placeholder="e.g. 110001, 201301, 560001"
                            value={pincodeText}
                            disabled={loading}
                            onChange={(e) => setPincodeText(e.target.value)}
                            status={parsedPincodes.length > 500 || hasNonNumericPincodes ? 'error' : undefined}
                            className="text-xs font-mono p-2.5 rounded-md border-gray-300 resize-none"
                        />
                        <span className={`text-[11px] mt-1 block ${parsedPincodes.length > 500 || hasNonNumericPincodes ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                            {hasNonNumericPincodes
                                ? `Pincodes must be numbers only. Invalid entries: ${nonNumericPincodes.slice(0, 4).join(', ')}${nonNumericPincodes.length > 4 ? ` +${nonNumericPincodes.length - 4} more` : ''}.`
                                : parsedPincodes.length > 500
                                ? `Exceeded limit of 500 Pincodes (${parsedPincodes.length} entered). Please remove ${parsedPincodes.length - 500} Pincodes.`
                                : 'Enter up to 500 numeric pincodes separated only by commas.'}
                        </span>
                    </div>
                </div>

                {/* Options: COD and Serviceable */}
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3.5 rounded-lg border border-gray-200">
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-2">
                            Cash On Delivery (COD)
                        </label>
                        <Radio.Group
                            value={isCod}
                            disabled={loading}
                            onChange={(e) => setIsCod(e.target.value)}
                        >
                            <Radio value={true}>Yes (Enabled)</Radio>
                            <Radio value={false}>No (Prepaid only)</Radio>
                        </Radio.Group>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-2">
                            Is Serviceable
                        </label>
                        <Radio.Group
                            value={isServiceable}
                            disabled={loading}
                            onChange={(e) => setIsServiceable(e.target.value)}
                        >
                            <Radio value={true}>Yes (Serviceable)</Radio>
                            <Radio value={false}>No (Non-serviceable)</Radio>
                        </Radio.Group>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ServiceabilityCreateModal;
